'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { fetchCountries, uploadFile } from '@eliadmin/core/src/api/general/general-api';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';

import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';

import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore, useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';

import { useMutation, useQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';

import { Heading } from '../ui/heading';

import { Field } from '../form/labeled-field';

import { ButtonIcon } from '../ui/button-icon';
import { InputImage } from '../ui/input-image';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Button } from '../ui/button';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { Separator } from '../ui/separator';

type ProfileEditFormValues = z.infer<ReturnType<typeof profileEditSchema>>;

export function ProfileEditForm() {
  const customer = useCustomerStore(s => s.me!.customer);
  const authToken = useCustomerStore(s => s.me!.authToken);

  const [file, setFile] = useState<File | undefined>(undefined);

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { data: countries = { data: [] } } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ProfileEditFormValues>({
    resolver: zodResolver(profileEditSchema(formValidationMessages)),
    defaultValues: {
      name: customer?.name ?? '',
      surname: customer?.surname ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? '',
      gender: customer?.gender ?? undefined,
      address: customer?.address ?? '',
      street_number: customer?.street_number ?? '',
      company_buy: customer?.company_buy ?? false,
      city: customer?.city ?? '',
      postcode: customer?.postcode ?? '',
      country: customer?.country ?? undefined,
      iban: customer?.iban ?? '',
      swift: customer?.swift ?? '',
      address_delivery: customer?.address_delivery ?? '',
      street_number_delivery: customer?.street_number_delivery ?? '',
      postcode_delivery: customer?.postcode_delivery ?? '',
      city_delivery: customer?.city_delivery ?? '',
      country_delivery: customer?.country_delivery ?? undefined,
      company: customer?.company ?? '',
      ico: customer?.ico ?? '',
      dic: customer?.dic ?? '',
      ic_dph: customer?.ic_dph ?? '',
      // Defaults to true so delivery fields are hidden by default
      same_billing_address: customer?.same_billing_address ?? true,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: updateCustomer,
  });

  const handleSubmit = form.handleSubmit(async data => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await uploadFile({
          authToken,
          formData,
          locale,
        });
        data.image = res.data;
      } catch (e) {
        //TODO: handle error / create custom field for this error
        return;
      }
    }

    try {
      await mutateAsync({
        locale,
        data: data as any,
        authToken,
      }).then(res => {
        const typedRes = res as { data: Customer };
        typedRes &&
          initCustomerStore({
            authToken,
            customer: { ...typedRes?.data },
          });
        return typedRes;
      });
      toast({
        variant: 'success',
        title: t('text4071'),
      });
      router.refresh();
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
    }
  });

  const fullName = `${customer.name} ${customer.surname}`;

  const sameBilling = form.watch('same_billing_address');

  return (
    <Form {...form}>
      <form
        className="mx-auto flex w-full max-w-[778px] flex-col items-center justify-center gap-6 sm:gap-8"
        onSubmit={handleSubmit}
      >
        <div className="flex w-full flex-col items-center justify-center gap-3 rounded-[12px] border border-border p-4 sm:p-6 lg:gap-4 lg:p-8">
          <Image
            src={'/images/icons/user-square.svg'}
            alt={fullName}
            width={48}
            height={48}
            className="shrink-0 object-contain"
          />
          <div className="flex w-full flex-col items-center justify-center gap-3">
            <Heading
              title={fullName}
              className={{ container: 'items-center justify-center text-center' }}
            />
            <div className="flex w-full items-center justify-center gap-2">
              <ButtonIcon url="/images/icons/email-primary.svg" alt="Email" />
              <span className="text-center text-sm font-semibold tracking-tight text-primary sm:text-base">
                {customer.email}
              </span>
            </div>
          </div>
        </div>
        {/* Contact fields */}
        <div className="grid w-full grid-cols-12 gap-x-2 gap-y-4 sm:gap-x-3 lg:gap-x-4">
          <SectionHeader title={t('text360')} />
          <Field
            required
            control={form.control}
            name="phone"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            type="tel"
            label={t('text1258')}
          />
          <Field
            control={form.control}
            name="email"
            type="email"
            label="Email"
            placeholder="Email"
            isDisabled={true}
            className={{ wrapper: 'col-span-12 w-full opacity-50 sm:col-span-6' }}
            before={<InputImage src="/images/icons/form/email.svg" alt="E-mail" />}
          />
        </div>
        <Separator className="w-full bg-border" />
        <div className="grid w-full grid-cols-12 gap-x-2 gap-y-4 sm:gap-x-3 lg:gap-x-4">
          <SectionHeader title={t('text3560')} />
          <FormField
            control={form.control}
            name="company_buy"
            render={({ field }) => (
              <FormItem
                className={cn('ea-register-form__company-type-field', 'col-span-12 w-full')}
              >
                <FormControl>
                  <div className="grid w-full grid-cols-2 gap-3">
                    <div
                      onClick={() => field.onChange(false)}
                      className={cn(
                        'col-span-1 flex w-full cursor-pointer flex-row items-center justify-start gap-3 rounded-[12px] p-4 text-sm font-medium tracking-tight text-black sm:text-base',
                        field.value === false
                          ? 'border-2 border-primary bg-primary-foreground'
                          : 'border border-border',
                      )}
                    >
                      <Image
                        src={'/images/icons/form/company-false.svg'}
                        alt={t('text2234')}
                        width={24}
                        height={24}
                        className="shrink-0"
                      />
                      <p>{t('text2234')}</p>
                    </div>

                    <div
                      onClick={() => field.onChange(true)}
                      className={cn(
                        'col-span-1 flex w-full cursor-pointer flex-row items-center justify-start gap-3 rounded-[12px] p-4 text-sm font-medium tracking-tight text-black sm:text-base',
                        field.value === true
                          ? 'border-2 border-primary bg-primary-foreground'
                          : 'border border-border',
                      )}
                    >
                      <Image
                        src={'/images/icons/form/company-true.svg'}
                        alt={t('text1432')}
                        width={24}
                        height={24}
                        className="shrink-0"
                      />
                      <p>{t('text1432')}</p>
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.watch('company_buy') === true ? (
            <CompanyDataFields form={form} countries={countries.data} />
          ) : (
            <PersonalDataFields form={form} countries={countries.data} />
          )}
        </div>
        <div className="grid w-full grid-cols-12 gap-x-6 gap-y-4">
          <FormField
            control={form.control}
            name="same_billing_address"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'col-span-12 flex w-full flex-row items-center justify-start gap-2',
                  'ea-register-form__same-billing-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="text-sm font-medium tracking-tight sm:text-base">
                  {t('text3705')}
                </FormLabel>
              </FormItem>
            )}
          />
        </div>
        {!sameBilling && (
          <div className="grid w-full grid-cols-12 gap-x-6 gap-y-4">
            <DeliveryDataFields form={form} />
          </div>
        )}
        <div className="col-span-12 flex w-full items-center justify-center">
          <Button
            type="submit"
            className="w-full bg-[#57B455] text-white hover:bg-[#57B455]/90 md:w-auto"
            isLoading={form.formState.isSubmitting}
          >
            {t('text2290')}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <Heading
      title={title}
      tag="h5"
      className={{ container: 'col-span-12 w-full items-start justify-start text-start' }}
    />
  );
}

function PersonalDataFields({ form, countries }: { form: any; countries: Country[] }) {
  return (
    <>
      <Field
        control={form.control}
        name="name"
        label="Meno"
        placeholder="Meno"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="surname"
        label="Priezvisko"
        placeholder="Priezvisko"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="address"
        label="Adresa"
        placeholder="Adresa"
        className={{ wrapper: 'col-span-8 w-full' }}
      />
      <Field
        control={form.control}
        name="street_number"
        label="Orientačné číslo"
        placeholder="Orientačné číslo"
        className={{ wrapper: 'col-span-4 w-full' }}
      />
      <Field
        control={form.control}
        name="postcode"
        label="Súpisné číslo"
        placeholder="Súpisné číslo"
        className={{ wrapper: 'col-span-4 w-full' }}
      />
      <Field
        control={form.control}
        name="city"
        label="Mesto"
        placeholder="Mesto"
        className={{ wrapper: 'col-span-8 w-full' }}
      />
      <Field
        control={form.control}
        name="country.id"
        label="Krajina"
        placeholder="Krajina"
        type="select"
        options={countries.map(c => ({ label: c.name, value: c.id }))}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
    </>
  );
}

function CompanyDataFields({ form, countries }: { form: any; countries: Country[] }) {
  return (
    <>
      <Field
        control={form.control}
        name="name"
        label="Meno kontaktnej osoby"
        placeholder="Meno kontaktnej osoby"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="surname"
        label="Priezvisko kontaktnej osoby"
        placeholder="Priezvisko kontaktnej osoby"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="company"
        label="Názov spoločnosti"
        placeholder="Názov spoločnosti"
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        control={form.control}
        name="ico"
        label="IČO"
        placeholder="IČO"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="dic"
        label="DIČ"
        placeholder="DIČ"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="ic_dph"
        label="IČ DPH"
        placeholder="IČ DPH"
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        control={form.control}
        name="address_company"
        label="Adresa spoločnosti"
        placeholder="Adresa spoločnosti"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        control={form.control}
        name="street_number_company"
        label="Orientačné číslo"
        placeholder="Orientačné číslo"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        control={form.control}
        name="postcode_company"
        label="Súpisné číslo"
        placeholder="Súpisné číslo"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        control={form.control}
        name="city_company"
        label="Mesto"
        placeholder="Mesto"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        control={form.control}
        name="country.id"
        label="Krajina"
        placeholder="Krajina"
        type="select"
        options={countries.map(c => ({ label: c.name, value: c.id }))}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
    </>
  );
}

function DeliveryDataFields({ form }: { form: any }) {
  return (
    <>
      <SectionHeader title="Adresa doručenia" />
      <Field
        control={form.control}
        name="name_delivery"
        label="Meno"
        placeholder="Meno"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="surname_delivery"
        label="Priezvisko"
        placeholder="Priezvisko"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        control={form.control}
        name="address_delivery"
        label="Adresa"
        placeholder="Adresa"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        control={form.control}
        name="street_number_delivery"
        label="Orientačné číslo"
        placeholder="Orientačné číslo"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        control={form.control}
        name="postcode_delivery"
        label="Súpisné číslo"
        placeholder="Súpisné číslo"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        control={form.control}
        name="city_delivery"
        label="Mesto"
        placeholder="Mesto"
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

export const profileEditSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    phone: z.string().optional(),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    username: z.string().optional(),
    address: z.string().optional(),
    street_number: z.string().optional(),
    postcode: z.string().optional(),
    city: z.string().optional(),
    country: z
      .object({
        id: z.string().or(z.number()).optional(),
      })
      .optional(),
    company_buy: z.boolean().optional(),
    same_billing_address: z.boolean().optional(),
    company: z.string().optional(),
    ico: z.string().optional(),
    dic: z.string().optional(),
    ic_dph: z.string().optional(),
    gender: z
      .object({
        id: z.string().or(z.number()).optional(),
      })
      .optional(),
    image: z
      .object({
        id: z.number(),
      })
      .optional(),
    address_temporary: z.string().optional(),
    street_number_temporary: z.string().optional(),
    postcode_temporary: z.string().optional(),
    city_temporary: z.string().optional(),
    country_temporary: z
      .object({
        id: z.string(),
      })
      .optional(),
    email_company: z.string().email().optional(),
    address_company: z.string().optional(),
    street_number_company: z.string().optional(),
    postcode_company: z.string().optional(),
    city_company: z.string().optional(),
    country_company: z
      .object({
        id: z.string(),
      })
      .optional(),
    address_delivery: z.string().optional(),
    street_number_delivery: z.string().optional(),
    postcode_delivery: z.string().optional(),
    city_delivery: z.string().optional(),
    country_delivery: z
      .object({
        id: z.string(),
      })
      .optional(),
    name_delivery: z.string().optional(),
    surname_delivery: z.string().optional(),
    company_delivery: z.string().optional(),
    iban: z.string().optional(),
    swift: z.string().optional(),
    instagram: z.string().optional(),
    facebook: z.string().optional(),
    national_id_number: z.string().optional(),
  });
